import { debounce } from 'lodash'
import { useEffect, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { IhiGender } from '../../../features/patient/PatientContextSlice'
import { LimitedPrescriberProfile } from '../../../features/users/UsersSlice'
import { Prescriber } from '../../../types'
import { useAppSelector } from '../../../app/typedReduxHooks'

const useIHIRevalidateFieldsChange = ({
  searchIhi,
  effectivePrescriber,
  medicareSearchParams,
  dvaSearchParams,
  resetValidation,
}: {
  searchIhi: (params: any) => void
  effectivePrescriber: LimitedPrescriberProfile | Prescriber | null
  medicareSearchParams?: {
    MedicareCardNumber: string
    MedicareIRN: string
  }
  dvaSearchParams?: {
    DvaFileNumber: string
  }
  resetValidation?: () => void
}) => {
  const { watch, trigger } = useFormContext()

  const { currentClinic } = useAppSelector((state) => state.clinic)

  useEffect(() => {
    const subscription = watch(async (values, { name }) => {
      const isTargetField = ['given_names', 'family_name', 'date_of_birth', 'gender'].includes(
        name as string
      )
      const isTargetFieldValid = await trigger([
        'given_names',
        'family_name',
        // 'date_of_birth', // date of birth validation has something wrong, so just comment it out for now
        'gender',
      ])

      const isMedicareValid =
        medicareSearchParams &&
        medicareSearchParams?.MedicareCardNumber &&
        medicareSearchParams?.MedicareIRN

      const isDvaValid = dvaSearchParams && dvaSearchParams?.DvaFileNumber

      if (!isMedicareValid && !isDvaValid) {
        return
      }
      if (isTargetField && isTargetFieldValid && values.date_of_birth) {
        resetValidation?.()
        debouncedValidateMedicareNumber({
          given_names: values.given_names,
          family_name: values.family_name,
          date_of_birth: values.date_of_birth,
          gender: values.gender,
          ...(isMedicareValid && { medicareSearchParams }),
          ...(isDvaValid && { dvaSearchParams }),
        })
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, trigger, medicareSearchParams, dvaSearchParams])

  const debouncedValidateMedicareNumber = useCallback(
    debounce(
      ({
        given_names,
        family_name,
        date_of_birth,
        gender,
        medicareSearchParams,
        dvaSearchParams,
      }) => {
        searchIhi({
          GivenName: given_names,
          FamilyName: family_name,
          DOB: date_of_birth,
          Gender: gender as IhiGender,
          HpiiNumber: effectivePrescriber?.hpii ?? '',
          ClinicId: currentClinic?.id,
          ...medicareSearchParams,
          ...dvaSearchParams,
        })
      },
      1000
    ),
    []
  )
}

export default useIHIRevalidateFieldsChange
